<template>
  <v-container pa-0>
    <v-dialog v-model="showDialog" max-width="750" click:outside="" persistent>
      <v-card>
        <v-card-title class="editor-title">
          <h2>{{ $vuetify.t('$vuetify.AdminUsers.titleAdd') }}</h2>
        </v-card-title>
        <v-card-text>
          <v-text-field
            v-model="firstName"
            v-validate="'required'"
            data-vv-as="first name"
            :error-messages="errors.collect('firstName')"
            :label="this.$vuetify.t('$vuetify.AdminUsers.formFirstName')"
            name="firstName"
            required
          ></v-text-field>
          <v-text-field
            v-model="lastName"
            v-validate="'required'"
            data-vv-as="last name"
            :error-messages="errors.collect('lastName')"
            :label="this.$vuetify.t('$vuetify.AdminUsers.formLastName')"
            name="lastName"
            required
          ></v-text-field>
          <v-text-field
            v-model="username"
            :label="usernameLabel"
            name="username"
          ></v-text-field>
          <v-text-field
            v-model="email"
            :error-messages="errors.collect('email')"
            :label="this.$vuetify.t('$vuetify.AdminUsers.formEmail')"
            name="email"
            required
          ></v-text-field>
          <v-select
            v-model="userRole"
            v-validate="'required'"
            data-vv-as="user role"
            :error-messages="errors.collect('userRole')"
            :items="userRoles"
            :label="this.$vuetify.t('$vuetify.AdminUsers.formGroup')"
            item-text="name"
            item-value="id"
            name="userRole"
            required
          ></v-select>
          <v-select
            v-model="ideaSpace"
            :items="ideaSpaces"
            :label="this.$vuetify.t('$vuetify.AdminUsers.formIdeaSpace')"
            name="ideaSpace"
          ></v-select>
        </v-card-text>
        <v-card-actions>
          <v-btn
            color="primary"
            @click="submit"
          >{{ $vuetify.t('$vuetify.AdminUsers.formCreate') }}
          </v-btn>
          <v-btn @click="closeDialog(false)">
            {{ $vuetify.t('$vuetify.AdminUsers.formCancel') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>

import api from '@/api'
import {getIdeaSpaces} from '../../utils/ideaSpaces'
import {getUserRoles} from '../../utils/userRoles'

export default {
  $_veeValidate: {validator: 'new'},
  name: 'UserAddDialog',
  props: {
    show: Boolean,
    users: Array
  },

  data () {
    return {
      firstName: '',
      lastName: '',
      username: '',
      userRole: null,
      ideaSpace: null,
      autoUsername: '',
      email: '',
      usernameLabel: this.genUsernameLabel(),
      ideaSpaces: [],
      userRoles: []
    }
  },

  beforeMount: async function () {
    this.userRoles = await getUserRoles()
    this.ideaSpaces = await getIdeaSpaces(this.$store.getters.selected_school, this.$vuetify.t)
  },

  computed: {
    showDialog: {
      get () {
        return this.show
      },
      set (value) {
        this.$emit('handleClose', value)
      }
    },

    allUsernames: function () {
      const existing = this.users == null ? [] : this.users.map(u => u.username)
      const fromCsv = this.csvUsers == null ? [] : this.csvUsers.map(u => u.username)
      return existing.concat(fromCsv)
    }
  },

  watch: {
    lastName: function () {
      this.usernameLabel = this.genUsernameLabel()
    },
    firstName: function () {
      this.usernameLabel = this.genUsernameLabel()
    }
  },

  methods: {
    genUsername: function (first, last, isCSVImport) {
      let candidate
      const firstName = first
      const lastName = last

      var stopSearchingUsername = false
      var sliceSize = 3
      var shortFirstname = firstName.slice(0, sliceSize)
      var shortLastname = lastName.slice(0, sliceSize)
      var letterVariant = 97
      var lettersIndex = 0
      while (!stopSearchingUsername) {
        candidate = (shortFirstname + shortLastname).toLowerCase()
        if (!this.allUsernames.includes(candidate)) {
          if (isCSVImport) {
            this.allUsernames.push(candidate)
          }
          return candidate
        } else {
          shortLastname += String.fromCharCode(letterVariant + lettersIndex++)
        }

        if (lettersIndex >= 26) {
          stopSearchingUsername = true
        }
      }

      // Fallback if no unique username can be generated
      // random number between 100 and 999
      return Math.floor((Math.random() * 899) + 100)
    },

    genUsernameLabel: function () {
      const un = this.username
      const ln = this.lastName

      return (un == null || un.length === 0) && ln && ln.length > 1
        ? this.$vuetify.t(
          '$vuetify.AdminUsers.formLoginSuggestion',
          this.genUsername(this.firstName, this.lastName, false)
        )
        : this.$vuetify.t('$vuetify.AdminUsers.formLogin')
    },

    closeDialog: function (hasUsersChanged) {
      if (hasUsersChanged) {
        this.$emit('reloadUsers')
      }
      this.showDialog = false
    },

    submit: function () {
      this.$validator.validate()
        .then(isFormValid => {
          // Do nothing if validation fails -  errors are displayed in UI
          if (!isFormValid) {
            this.showSnackbar = true
            this.snackbarMsg = this.$vuetify.t('$vuetify.Snackbar.formError')
            return
          }

          // Submit the entered username if it has more than one
          // character, otherwise default to the autogenerated username
          const username = this.username && this.username.length > 1
            ? this.username
            : this.genUsername(this.firstName, this.lastName, false)

          const email = this.email && this.email.length > 0
            ? this.email
            : null

          const user = {
            first_name: this.firstName,
            last_name: this.lastName,
            email,
            username,
            school: this.$store.getters.selected_school,
            user_role: this.userRole,
            idea_space: this.ideaSpace
          }

          // Select the appripriate api depending on whether
          // a user is being added or edited. Also fill
          // in the user id and remove fields only used for new users
          // when editing.
          let fn = api.user.create

          fn(user)
            .then((res) => {
              if (res.status < 400) {
                this.showSnackbar = true
                this.snackbarMsg = this.editingId == null
                  ? this.$vuetify.t('$vuetify.AdminUsers.snackbarCreated', 1)
                  : this.$vuetify.t('$vuetify.AdminUsers.snackbarSaved')
                this.closeDialog(true)
              } else {
                this.showSnackbar = true
                this.snackbarMsg = this.$vuetify.t(
                  '$vuetify.Snackbar.serverError')
              }
            })
            .catch((err) => {
              // 23505 is the Postgrest error code for violations of
              // unique constraints
              if (err.response && err.response.data.code === '23505') {
                this.errors.add({
                  field: 'username',
                  msg: this.$vuetify.t(
                    '$vuetify.AdminUsers.formLoginUniqueError', user.username)
                })
              } else {
                this.showSnackbar = true
                this.snackbarMsg = this.$vuetify.t(
                  '$vuetify.Snackbar.clientError')
              }
            })
        })
    }
  }
}
</script>

<style scoped>

</style>
